// Claude: New component for specification.md Phase 2 item 3 — reviewer-facing approve/reject/flag
// Claude: UI for words awaiting review (backend: /v1/review-queue/, /v1/review-word/<id>/).
import React, { useState, useEffect, useContext } from "react";
import Modal from "react-modal";
import languageMapper, { baseUrl, getValidAccessToken } from "../shared";
import Logo from "../logo512.png";
import Navbar from "../navbar";
import Navbaranonuser from "../navbaranonuser";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";

// Claude: new for UAT feedback ("the review queue also needs a pop-up modal... display the
// Claude: information of the word under consideration and the English reference word"). Same
// Claude: field list DuplicateCleanup.js's comparison pop-up already uses (see DETAIL_FIELDS
// Claude: there) — status is dropped here since it's redundant (every row in this queue is PND
// Claude: by definition) and the decision buttons already act on it.
const DETAIL_FIELDS = [
  { key: "pos", labelKey: "pos" },
  { key: "definition", labelKey: "definition" },
  { key: "example", labelKey: "example" },
  { key: "attribution", labelKey: "attribution" },
  { key: "field", labelKey: "context" },
  { key: "school", labelKey: "school" },
  { key: "synonyms", labelKey: "synonyms" },
  { key: "editor", labelKey: "editor" },
  { key: "date_edited", labelKey: "date_edited_label" },
  // Claude: notes field (alpha-tester request) — reviewers need to see an entry's note.
  { key: "notes", labelKey: "notes" },
];

// Claude: the fields a reviewer can make "minor changes" to before approving/flagging/rejecting
// Claude: (per description.txt, editors/approvers may edit every field except status — status
// Claude: changes stay on the existing Approve/Reject/Flag buttons, not this form). Reuses the
// Claude: existing PUT /v1/updateword/<id>/ endpoint (UpdateWord.js's own endpoint) — a partial
// Claude: update, so only these fields are sent.
const EDIT_FIELDS = [
  { key: "word", labelKey: "word" },
  { key: "pos", labelKey: "pos" },
  { key: "definition", labelKey: "definition" },
  { key: "example", labelKey: "example" },
  { key: "attribution", labelKey: "attribution" },
  { key: "field", labelKey: "context" },
  { key: "school", labelKey: "school" },
  { key: "synonyms", labelKey: "synonyms" },
  // Claude: notes field (alpha-tester request) — editable here like any other field (textarea).
  { key: "notes", labelKey: "notes" },
];

// Claude: bugfix reported during UAT — the PoS field in this edit form was a freeform text input,
// Claude: letting a reviewer type anything. Same fixed option set/values UpdateWord.js's own PoS
// Claude: <select> already uses, reused here rather than invented fresh so the two edit paths stay
// Claude: consistent with each other.
const POS_OPTIONS = [
  { value: "Unknown", labelKey: "unknown" },
  { value: "Noun", labelKey: "noun" },
  { value: "Verb", labelKey: "verb" },
  { value: "Adjective", labelKey: "adjective" },
  { value: "Pronoun", labelKey: "pronoun" },
  { value: "Adverb", labelKey: "adverb" },
  { value: "Preposition", labelKey: "preposition" },
  { value: "Conjunction", labelKey: "conjunction" },
  { value: "Article", labelKey: "article" },
  { value: "Interjection", labelKey: "interjection" },
];

// Claude: bugfix found during UAT — the backend used to return every PENDING word with no limit
// Claude: at all, which crashed against the real dataset (~93k rows, most of them PENDING) and
// Claude: would be unusable even once fixed (a 93k-row response/table). The backend now paginates
// Claude: (?limit=&offset=, see review_queue in views.py); this component pages through it with
// Claude: Previous/Next rather than trying to render everything at once.
const REVIEW_QUEUE_PAGE_SIZE = 50;

// Claude: same 13-language code list MissingTerms.js already uses for its target-language select
// Claude: (Word.language's fixed choices) — reused here for the language filter.
const LANGUAGE_OPTIONS = [
  { code: "Eng", label: "English" },
  { code: "Afr", label: "Afrikaans" },
  { code: "Zul", label: "isiZulu" },
  { code: "Ses", label: "Sesotho" },
  { code: "Sep", label: "Sepedi" },
  { code: "Set", label: "Setswana" },
  { code: "Xit", label: "Xitsonga" },
  { code: "Sis", label: "siSwati" },
  { code: "Nde", label: "isiNdebele" },
  { code: "Xho", label: "isiXhosa" },
  { code: "Tsh", label: "Tshivenḓa" },
  { code: "Por", label: "Portuguese" },
  { code: "Fre", label: "French" },
];

// Claude: sortable-headers feature added per UAT feedback ("the review list should be sortable by
// Claude: clicking on any of the headers"). Each column maps to the backend field review_queue's
// Claude: ?sort_by= accepts (see REVIEW_QUEUE_SORT_FIELDS in views.py). "Days pending" is the odd
// Claude: one out: there's no such field on Word, it's derived from date_edited, and the
// Claude: relationship is inverted — the *oldest* date_edited is the *most* days pending — so
// Claude: invert:true flips asc/desc before it's sent to the backend, keeping the header's arrow
// Claude: intuitive (ascending really does mean fewest days pending first). Labels are looked up
// Claude: via t() at render time (see COLUMN_LABEL_KEYS) rather than stored here, since this array
// Claude: is defined once at module scope (outside the component, so it has no access to t()).
const REVIEW_QUEUE_COLUMNS = [
  { key: "word", labelKey: "word", backendField: "word" },
  { key: "wordEnglish", labelKey: "english", backendField: "wordEnglish" },
  { key: "language", labelKey: "language", backendField: "language" },
  { key: "editor", labelKey: "submitted_by", backendField: "editor" },
  { key: "days_pending", labelKey: "days_pending", backendField: "date_edited", invert: true },
];

const ReviewQueue = () => {
  const [pendingWords, setPendingWords] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [offset, setOffset] = useState(0);
  // Claude: matches the backend's own default (date_edited ascending = most days pending first).
  const [sortColumn, setSortColumn] = useState("days_pending");
  const [sortDirection, setSortDirection] = useState("desc");
  const [languageFilter, setLanguageFilter] = useState("");
  const [editorFilter, setEditorFilter] = useState("");
  // Claude: separate draft state for the "submitted by" text input — applied on Filter/Enter
  // Claude: rather than on every keystroke, since editor__icontains can't use an index (see
  // Claude: review_queue's docstring) and re-querying on each character would be wasteful.
  const [editorFilterDraft, setEditorFilterDraft] = useState("");
  const [error, setError] = useState(null);
  // Claude: new for UAT feedback — holds the whole pending-word object (already carries every
  // Claude: WordSerializer field plus english_reference, see review_queue in views.py) for the
  // Claude: comparison pop-up; null when the pop-up is closed.
  const [selectedWord, setSelectedWord] = useState(null);
  // Claude: toggles the pop-up between the read-only comparison view and the in-place edit form —
  // Claude: per UAT feedback ("even better, an inplace edit option in the modal... when the editing
  // Claude: is finished, it should return to the review modal").
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();

  const fetchPendingWords = async ({
    fetchOffset = offset,
    fetchSortColumn = sortColumn,
    fetchSortDirection = sortDirection,
    fetchLanguageFilter = languageFilter,
    fetchEditorFilter = editorFilter,
  } = {}) => {
    const token = await getValidAccessToken();
    const column = REVIEW_QUEUE_COLUMNS.find((c) => c.key === fetchSortColumn);
    const backendOrder = column.invert
      ? (fetchSortDirection === "asc" ? "desc" : "asc")
      : fetchSortDirection;
    try {
      const response = await fetch(
        baseUrl +
          `v1/review-queue/?limit=${REVIEW_QUEUE_PAGE_SIZE}&offset=${fetchOffset}` +
          `&sort_by=${column.backendField}&order=${backendOrder}` +
          `&language=${encodeURIComponent(fetchLanguageFilter)}&editor=${encodeURIComponent(fetchEditorFilter)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP status ${response.status}`);
      }
      const data = await response.json();
      setPendingWords(data.pending_words);
      setTotalPending(data.total_pending);
      setOffset(data.offset);
      setSortColumn(fetchSortColumn);
      setSortDirection(fetchSortDirection);
      setLanguageFilter(fetchLanguageFilter);
      setEditorFilter(fetchEditorFilter);
    } catch (err) {
      console.error("Error:", err);
      // Claude: alpha-tester feedback — "the error message is displayed... but there is no link to
      // Claude: the login page." Same pattern UserRoleManager.js already uses.
      setError(
        <>
          Error fetching the review queue — please{' '}
          <Link to="/login" className="text-blue-500 underline hover:text-blue-700">log in</Link> again.
        </>
      );
    }
  };

  useEffect(() => {
    fetchPendingWords({ fetchOffset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (columnKey) => {
    const nextDirection = columnKey === sortColumn && sortDirection === "asc" ? "desc" : "asc";
    // Claude: sorting/filtering changes what "page 2" even means, so go back to the first page.
    fetchPendingWords({ fetchOffset: 0, fetchSortColumn: columnKey, fetchSortDirection: nextDirection });
  };

  const handleLanguageFilterChange = (e) => {
    fetchPendingWords({ fetchOffset: 0, fetchLanguageFilter: e.target.value });
  };

  const applyEditorFilter = () => {
    fetchPendingWords({ fetchOffset: 0, fetchEditorFilter: editorFilterDraft });
  };

  const clearFilters = () => {
    setEditorFilterDraft("");
    fetchPendingWords({ fetchOffset: 0, fetchLanguageFilter: "", fetchEditorFilter: "" });
  };

  const handleDecision = async (id, decision, onAfterClick = () => {}) => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + `v1/review-word/${id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ decision }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchPendingWords({ fetchOffset: offset });
        onAfterClick();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  // Claude: shared between the table row and the pop-up, same pattern as DuplicateCleanup.js's
  // Claude: renderDecisionButtons — onAfterClick lets the pop-up close itself once a decision is
  // Claude: made, since the word it was showing is about to disappear from the queue either way.
  // Claude: UAT feedback — "Only administrators and approvers should be able to change the status
  // Claude: of a word to accepted or rejected... Flagged should be available only to
  // Claude: administrators, approvers and editors." An editor can now reach this page at all (see
  // Claude: navbar.js/review_queue in views.py) specifically to use Flag — Approve/Reject stay
  // Claude: hidden for them, matching what review_word would actually let them do server-side.
  const canApproveOrReject = auth.userRole === "approver" || auth.userRole === "administrator";
  const renderDecisionButtons = (word, onAfterClick = () => {}) => (
    <>
      {canApproveOrReject && (
        <>
          <button
            onClick={() => handleDecision(word._id, "approve", onAfterClick)}
            className="bg-cyan-700 text-white py-1 px-3 rounded-md text-sm hover:bg-cyan-800"
          >
            {t("approve")}
          </button>
          {" "}
          <button
            onClick={() => handleDecision(word._id, "reject", onAfterClick)}
            className="bg-rose-700 text-white py-1 px-3 rounded-md text-sm hover:bg-rose-800"
          >
            {t("reject")}
          </button>
          {" "}
        </>
      )}
      <button
        onClick={() => handleDecision(word._id, "flag", onAfterClick)}
        className="bg-gray-700 text-white py-1 px-3 rounded-md text-sm hover:bg-gray-800"
      >
        {t("flag_action")}
      </button>
    </>
  );

  const closeWordModal = () => {
    setSelectedWord(null);
    setIsEditing(false);
  };

  const startEditing = () => {
    setEditForm(
      Object.fromEntries(EDIT_FIELDS.map((field) => [field.key, selectedWord[field.key] || ""]))
    );
    setIsEditing(true);
  };

  // Claude: discards the in-progress edits and returns to the read-only comparison view — per UAT
  // Claude: feedback ("when the editing is finished, it should return to the review modal"), a
  // Claude: cancel is just as much "finished" as a save.
  const cancelEditing = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + `v1/updateword/${selectedWord._id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${JSON.stringify(data)}`);
        return;
      }
      const updatedWord = { ...selectedWord, ...editForm };
      setSelectedWord(updatedWord);
      setPendingWords((prev) => prev.map((word) => (word._id === updatedWord._id ? updatedWord : word)));
      setIsEditing(false);
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  const hasPrevious = offset > 0;
  const hasNext = offset + REVIEW_QUEUE_PAGE_SIZE < totalPending;
  const hasActiveFilters = languageFilter !== "" || editorFilter !== "";

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {auth.userName === null && <Navbaranonuser />}
      {auth.userName != null && <Navbar />}

      <div className="flex flex-col justify-center items-center p-2">
        <h3 className="text-4xl font-bold mb-6">
          <Link to={"/"}>{t("review_queue")}</Link>
        </h3>

        {/* Claude: filtering added per UAT feedback ("add a filter option for 'language' and
            'submitted by'"). Language applies immediately on change (matches MissingTerms.js's
            own language select); "submitted by" applies via the Filter button or Enter, since it's
            a partial-text match re-queried on the backend rather than a fixed small set of values. */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label htmlFor="review-queue-language-filter" className="block text-sm text-gray-700">
              {t("filter_language")}
            </label>
            <select
              id="review-queue-language-filter"
              value={languageFilter}
              onChange={handleLanguageFilterChange}
              className="border p-2 rounded-lg"
            >
              <option value="">{t("all_languages_filter")}</option>
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="review-queue-editor-filter" className="block text-sm text-gray-700">
              {t("filter_submitted_by")}
            </label>
            <input
              id="review-queue-editor-filter"
              type="text"
              value={editorFilterDraft}
              onChange={(e) => setEditorFilterDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyEditorFilter(); }}
              className="border p-2 rounded-lg"
            />
          </div>
          <button
            onClick={applyEditorFilter}
            className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800"
          >
            {t("filter")}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800"
            >
              {t("clear_filters")}
            </button>
          )}
        </div>

        {pendingWords.length === 0 && <p>{t("nothing_waiting_review")}</p>}
        {totalPending > 0 && (
          <p>
            {t("showing_range", { start: offset + 1, end: offset + pendingWords.length, total: totalPending })}
          </p>
        )}
        <table className="border-separate [border-spacing:0.8rem]">
          <thead>
            <tr>
              {REVIEW_QUEUE_COLUMNS.map((column) => (
                <th key={column.key}>
                  <button
                    onClick={() => handleSort(column.key)}
                    className="font-semibold hover:text-cyan-700 inline-flex items-center gap-1"
                  >
                    {t(column.labelKey)}
                    {sortColumn === column.key && (
                      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </button>
                </th>
              ))}
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {pendingWords.map((word) => (
              <tr key={word._id} className={word.is_stale ? "bg-red-100" : ""}>
                <td>
                  {/* Claude: new for UAT feedback ("the review queue also needs a pop-up modal...
                      display the information of the word under consideration and the English
                      reference word"). The full record (plus english_reference) is already in
                      this row's data — see review_queue in views.py — so opening the pop-up needs
                      no extra fetch. */}
                  <button
                    type="button"
                    onClick={() => setSelectedWord(word)}
                    className="text-cyan-700 underline hover:text-cyan-800"
                  >
                    {word.word}
                  </button>
                </td>
                <td>{word.wordEnglish}</td>
                {/* Claude: bugfix reported during UAT — this showed the raw 3-letter language code
                    (e.g. "Afr") instead of the full name every other user-facing display in the
                    app already uses (see languageMapper in shared.js). */}
                <td>{languageMapper(word.language)}</td>
                <td>{word.editor}</td>
                <td>{word.days_pending ?? "unknown"}{word.is_stale ? " (stale)" : ""}</td>
                {/* Claude: bugfix reported during UAT — these were unstyled <button>s that didn't
                    read as buttons at all. Colors match the palette already used elsewhere
                    (cyan-700 for the primary/positive action, rose-700 for the destructive one,
                    gray-700 for the neutral one — see e.g. PhraseDetail.js's Update/Delete/No
                    buttons). Now shared with the pop-up via renderDecisionButtons. */}
                <td>{renderDecisionButtons(word)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(hasPrevious || hasNext) && (
          <div className="mt-4">
            <button
              onClick={() => fetchPendingWords({ fetchOffset: Math.max(0, offset - REVIEW_QUEUE_PAGE_SIZE) })}
              disabled={!hasPrevious}
              className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("previous")}
            </button>
            {" "}
            <button
              onClick={() => fetchPendingWords({ fetchOffset: offset + REVIEW_QUEUE_PAGE_SIZE })}
              disabled={!hasNext}
              className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>

      {/* Claude: new for UAT feedback ("the review queue also needs a pop-up modal... display
          the information of the word under consideration and the English reference word...
          make it clear that the English version is for reference only. There should also be an
          edit button... in-place edit option in the modal... when the editing is finished, it
          should return to the review modal"). Same react-modal pattern DuplicateCleanup.js's
          comparison pop-up already uses. */}
      <Modal
        isOpen={selectedWord !== null}
        onRequestClose={closeWordModal}
        appElement={document.getElementById("root")}
        className="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        {selectedWord && (
          <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 max-w-4xl max-h-[90vh] overflow-auto p-4">
            <button
              onClick={closeWordModal}
              className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600"
            >
              {t("back")}
            </button>
            <h2 className="text-xl font-semibold mt-4 mb-4">
              {selectedWord.word} ({languageMapper(selectedWord.language)})
            </h2>

            {isEditing ? (
              <div>
                {EDIT_FIELDS.map((field) => (
                  <div key={field.key} className="mb-3">
                    <label htmlFor={`review-edit-${field.key}`} className="block text-gray-700">
                      <strong>{t(field.labelKey)}:</strong>
                    </label>
                    {field.key === "pos" ? (
                      <select
                        id="review-edit-pos"
                        value={editForm.pos || ""}
                        onChange={(e) => setEditForm({ ...editForm, pos: e.target.value })}
                        className="border p-2 w-full"
                      >
                        {POS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                        ))}
                        {/* Claude: real data may already hold a value outside POS_OPTIONS (e.g.
                            different capitalisation, or free text from before this fix) — keep it
                            selectable as-is instead of silently swapping it for "Unknown" the
                            moment this form opens. */}
                        {editForm.pos && !POS_OPTIONS.some((option) => option.value === editForm.pos) && (
                          <option value={editForm.pos}>{editForm.pos}</option>
                        )}
                      </select>
                    ) : field.key === "notes" ? (
                      <textarea
                        id="review-edit-notes"
                        rows={3}
                        value={editForm.notes || ""}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        className="border p-2 w-full"
                      />
                    ) : (
                      <input
                        id={`review-edit-${field.key}`}
                        type="text"
                        value={editForm[field.key] || ""}
                        onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                        className="border p-2 w-full"
                      />
                    )}
                  </div>
                ))}
                <div className="mt-4">
                  <button
                    onClick={handleSaveEdit}
                    className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800"
                  >
                    {t("update")}
                  </button>
                  {" "}
                  <button
                    onClick={cancelEditing}
                    className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <table className="w-full border-separate [border-spacing:0.5rem]">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="text-left">{selectedWord.word}</th>
                      {selectedWord.english_reference && (
                        <th className="text-left text-gray-500 font-normal italic">
                          {t("english_reference_label")}: {selectedWord.english_reference.word}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {DETAIL_FIELDS.map((field) => (
                      <tr key={field.key}>
                        <th className="text-left align-top">{t(field.labelKey)}</th>
                        <td className={`align-top ${field.key === "notes" ? "whitespace-pre-line" : ""}`}>{selectedWord[field.key] || "—"}</td>
                        {selectedWord.english_reference && (
                          <td className="align-top text-gray-500 italic bg-gray-50">
                            {selectedWord.english_reference[field.key] || "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedWord.language !== "Eng" && !selectedWord.english_reference && (
                  <p className="text-gray-500 italic mt-2">{t("no_english_reference")}</p>
                )}
                <div className="mt-4">
                  {renderDecisionButtons(selectedWord, closeWordModal)}
                  {" "}
                  <button
                    onClick={startEditing}
                    className="bg-amber-700 text-white py-1 px-3 rounded-md text-sm hover:bg-amber-800"
                  >
                    {t("update")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <div className="flex flex-col justify-center items-center p-4">
        <Link to={"/"}>
          <img src={Logo} alt="Logo" width="100" height="100" />
        </Link>
      </div>
    </div>
  );
};

export default ReviewQueue;
